import GetLoading from "../../share/getLoading";
// component/DepartmentStaffModal.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Avatar,
  Tag,
  Input,
  Space,
  Button,
  Typography,
  Tooltip,
  Empty,
  Badge,
  Tabs,
  message,
} from "antd";
import {
  UserOutlined,
  SearchOutlined,
  TeamOutlined,
  ApartmentOutlined,
  CloseOutlined,
  MailOutlined,
  PhoneOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SaveOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { request } from "../../share/request";
import { linkPath } from "../../share/helper";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Font styles
const khmerFont = {
  fontFamily: "'Content', system-ui, sans-serif",
};

const englishFont = {
  fontFamily: "'Poppins-SemiBold', 'Poppins', 'Poppins SemiBold', system-ui, sans-serif",
};

const mixedFont = {
  fontFamily: "'Poppins-SemiBold', 'Content', system-ui, sans-serif",
};

const DepartmentStaffModal = ({
  visible,
  onCancel,
  department,
  org_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [originalStaffList, setOriginalStaffList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (visible && department) {
      fetchStaffList();
    }
  }, [visible, department]);

  const fetchStaffList = async () => {
    setLoading(true);
    try {
      const param = new URLSearchParams({
        org_code: org_code,
        department: department.code,
      });
      const res = await request(`user?${param.toString()}`, "get", {});
      if (res && res.listuser) {
        const sortedList = [...res.listuser].sort((a, b) => (a.order_no || 999) - (b.order_no || 999));
        setStaffList(sortedList);
        setOriginalStaffList(JSON.parse(JSON.stringify(sortedList)));
      } else if (department.staff_list) {
        const sortedList = [...department.staff_list].sort((a, b) => (a.order_no || 999) - (b.order_no || 999));
        setStaffList(sortedList);
        setOriginalStaffList(JSON.parse(JSON.stringify(sortedList)));
      } else {
        setStaffList([]);
        setOriginalStaffList([]);
      }
    } catch (error) {
      console.error("Error fetching staff list:", error);
      if (department.staff_list) {
        const sortedList = [...department.staff_list].sort((a, b) => (a.order_no || 999) - (b.order_no || 999));
        setStaffList(sortedList);
        setOriginalStaffList(JSON.parse(JSON.stringify(sortedList)));
      } else {
        setStaffList([]);
        setOriginalStaffList([]);
      }
    } finally {
      setLoading(false);
      setHasChanges(false);
    }
  };

  const moveStaffUp = (index) => {
    if (index === 0) return;
    const newStaffList = [...staffList];
    const temp = newStaffList[index];
    newStaffList[index] = newStaffList[index - 1];
    newStaffList[index - 1] = temp;
    setStaffList(newStaffList);
    setHasChanges(true);
  };

  const moveStaffDown = (index) => {
    if (index === staffList.length - 1) return;
    const newStaffList = [...staffList];
    const temp = newStaffList[index];
    newStaffList[index] = newStaffList[index + 1];
    newStaffList[index + 1] = temp;
    setStaffList(newStaffList);
    setHasChanges(true);
  };

 const saveOrder = async () => {
  if (!hasChanges) {
    message.info("No changes to save");
    return;
  }

  setSaving(true);
  try {
    const payload = {
      org_code: org_code,
      department_code: department.code,
      staff_order: staffList.map((staff, idx) => ({
        id: staff.id,
        rang_short: idx + 1,
      })),
    };
    console.log("Saving staff order with payload:", payload);

    // Change from "post" to "put" to match your route
    const res = await request("department/stafforder", "put", payload);
    if (res && res.success) {
      message.success("Order saved successfully");
      setOriginalStaffList(JSON.parse(JSON.stringify(staffList)));
      setHasChanges(false);
    } else {
      message.error(res?.message || "Failed to save order");
    }
  } catch (error) {
    console.error("Error saving order:", error);
    message.error("Failed to save order");
  } finally {
    setSaving(false);
  }
};
  const resetOrder = () => {
    setStaffList(JSON.parse(JSON.stringify(originalStaffList)));
    setHasChanges(false);
    message.info("Order reset to original");
  };

  const filteredStaff = staffList.filter((staff) => {
    const searchLower = searchText.toLowerCase();
    return (
      (staff.fullname || "").toLowerCase().includes(searchLower) ||
      (staff.username || "").toLowerCase().includes(searchLower) ||
      (staff.email || "").toLowerCase().includes(searchLower) ||
      (staff.phone || "").toLowerCase().includes(searchLower) ||
      (staff.position_kh || "").toLowerCase().includes(searchLower) ||
      (staff.jobstatus || "").toLowerCase().includes(searchLower) ||
      (staff.stafftype || "").toLowerCase().includes(searchLower)
    );
  });

  const activeStaff = filteredStaff.filter((staff) => staff.lock_user === 1);
  const inactiveStaff = filteredStaff.filter((staff) => staff.lock_user !== 1);

  const getStaffStatus = (lock_user) => {
    if (lock_user === 1) {
      return {
        color: "green",
        text: "Active",
        khmerText: "កំពុងដំណើរការ",
      };
    } else if (lock_user === 0) {
      return {
        color: "orange",
        text: "Locked",
        khmerText: "បានចាក់សោ",
      };
    } else {
      return {
        color: "default",
        text: "Inactive",
        khmerText: "មិនដំណើរការ",
      };
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'ធ្វើការ':
        return { color: "green", text: "ធ្វើរការ", en: "Working" };
      case 'ព្យួរ':
        return { color: "orange", text: "ព្យួរ", en: "Suspended" };
      case 'បណ្តុះបណ្តាល':
        return { color: "blue", text: "បណ្តុះបណ្តាល", en: "Training" };
      case 'ឈប់':
        return { color: "red", text: "ឈប់", en: "Stopped" };
      default:
        return { color: "default", text: status || "N/A", en: "N/A" };
    }
  };

  const getStaffTypeColor = (type) => {
    switch (type) {
      case 'រដ្ឋ':
        return { color: "purple", text: "រដ្ឋ", en: "Government" };
      case 'កិច្ចសន្យា':
        return { color: "cyan", text: "កិច្ចសន្យា", en: "Contract" };
      case 'ជួល':
        return { color: "geekblue", text: "ជួល", en: "Rental" };
      case 'ផ្សេងៗ':
        return { color: "default", text: "ផ្សេងៗ", en: "Other" };
      default:
        return { color: "default", text: type || "N/A", en: "N/A" };
    }
  };

  const columns = [
    {
      title: "ល.រ",
      key: "order",
      width: 60,
      render: (_, __, index) => (
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          {index + 1}
        </div>
      ),
    },
    {
      title: "រូបភាព",
      key: "image",
      width: 80,
      render: (_, record) => (
        <Avatar
          src={record.image ? `${linkPath}${record.image}` : null}
          icon={!record.image && <UserOutlined />}
          size="large"
          style={{
            backgroundColor: "#1890ff",
            border: "2px solid #fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          {!record.image && record.fullname ? record.fullname.charAt(0).toUpperCase() : null}
        </Avatar>
      ),
    },
    {
      title: "ឈ្មោះ",
      key: "name",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, ...khmerFont }}>{record.fullname || "N/A"}</div>
          <div style={{ fontSize: 12, color: "#666", ...khmerFont }}>{record.position_kh || "N/A"}</div>
        </div>
      ),
    },
    {
      title: "ស្ថានភាពការងារ",
      key: "jobstatus",
      width: 120,
      render: (_, record) => {
        const status = getJobStatusColor(record.jobstatus);
        return (
          <Tag color={status.color} style={mixedFont}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: "ប្រភេទបុគ្គលិក",
      key: "stafftype",
      width: 120,
      render: (_, record) => {
        const type = getStaffTypeColor(record.stafftype);
        return (
          <Tag color={type.color} style={mixedFont}>
            {type.text}
          </Tag>
        );
      },
    },
    {
      title: "ស្ថានភាព",
      key: "status",
      width: 100,
      render: (_, record) => {
        const status = getStaffStatus(record.lock_user);
        return (
          <Tag color={status.color} style={mixedFont}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: "រៀបចំ",
      key: "actions",
      width: 100,
      render: (_, __, index) => (
        <Space size={4}>
          <Tooltip title="Move Up">
            <Button
              type="text"
              size="small"
              icon={<ArrowUpOutlined />}
              onClick={() => moveStaffUp(index)}
              disabled={index === 0}
            />
          </Tooltip>
          <Tooltip title="Move Down">
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              onClick={() => moveStaffDown(index)}
              disabled={index === staffList.length - 1}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const smallColumns = [
    {
      title: "ល.រ",
      key: "order",
      width: 50,
      render: (_, __, index) => (
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          {index + 1}
        </div>
      ),
    },
    {
      title: "ឈ្មោះ",
      key: "name",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            src={record.image ? `${linkPath}${record.image}` : null}
            icon={!record.image && <UserOutlined />}
            size="default"
            style={{ backgroundColor: "#1890ff" }}
          >
            {!record.image && record.fullname ? record.fullname.charAt(0).toUpperCase() : null}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500, ...khmerFont }}>{record.fullname || "N/A"}</div>
            <div style={{ fontSize: 11, color: "#999", ...khmerFont }}>{record.position_kh || "N/A"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "ស្ថានភាពការងារ",
      key: "jobstatus",
      width: 90,
      render: (_, record) => {
        const status = getJobStatusColor(record.jobstatus);
        return (
          <Tag color={status.color} style={{ fontSize: 10, ...khmerFont }}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: "ប្រភេទ",
      key: "stafftype",
      width: 80,
      render: (_, record) => {
        const type = getStaffTypeColor(record.stafftype);
        return (
          <Tag color={type.color} style={{ fontSize: 10, ...khmerFont }}>
            {type.text}
          </Tag>
        );
      },
    },
    {
      title: "ស្ថានភាព",
      key: "status",
      width: 70,
      render: (_, record) => {
        const status = getStaffStatus(record.lock_user);
        return (
          <Tag color={status.color} style={{ fontSize: 10 }}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: "រៀបចំ",
      key: "actions",
      width: 70,
      render: (_, __, index) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            onClick={() => moveStaffUp(index)}
            disabled={index === 0}
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            onClick={() => moveStaffDown(index)}
            disabled={index === staffList.length - 1}
          />
        </Space>
      ),
    },
  ];

  const renderStaffTable = (data) => (
    <Table
      columns={window.innerWidth < 768 ? smallColumns : columns}
      dataSource={data}
      rowKey="id"
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `សរុប ${total} នាក់`,
      }}
      locale={{
        emptyText: <Empty description="មិនមានបុគ្គលិក / No staff" />,
      }}
      size="middle"
      scroll={{ x: true }}
    />
  );

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ApartmentOutlined style={{ fontSize: 24, color: "#fff" }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, ...khmerFont }}>
                {department?.department_name || "Department"}
              </Title>
              <div style={{ fontSize: 12, color: "#666", ...englishFont }}>
                {department?.department_en || ""}
                <Tag color="blue" style={{ marginLeft: 8, ...khmerFont }}>
                  Code: {department?.code}
                </Tag>
              </div>
            </div>
          </div>
          {hasChanges && (
            <div style={{ display: "flex", gap: 8, marginRight: 30 }}>
              <Tooltip title="Reset to original order">
                <Button icon={<ReloadOutlined />} onClick={resetOrder} />
              </Tooltip>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={saveOrder}
                loading={saving}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
              >
                Save Order
              </Button>
            </div>
          )}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
       <Button 
  key="close" 
  onClick={onCancel} 
  icon={<CloseOutlined />} 
  style={{ backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "#fff", ...mixedFont }}
>
  បិទ / Close
</Button>
      ]}
      style={{ top: 20 }}
    >
      <div style={{ padding: "8px 0" }}>
        {/* Department Info Summary */}
        <div
          style={{
            display: "flex",
            gap: 16,
            padding: "12px 16px",
            background: "#f0f7ff",
            borderRadius: 12,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <Text type="secondary" style={khmerFont}>
              ផ្នែកមេ :
            </Text>
            <div style={{ fontWeight: 500, ...khmerFont }}>{department?.parent_dept || "កម្រិតកំពូល"}</div>
          </div>
          <div>
            <Text type="secondary" style={khmerFont}>
              លំដាប់ :
            </Text>
            <div style={{ fontWeight: 500, ...khmerFont }}>{department?.range_id || "N/A"}</div>
          </div>
          <div>
            <Text type="secondary" style={khmerFont}>
              បុគ្គលិកសរុប :
            </Text>
            <div style={{ fontWeight: 600, fontSize: 18, color: "#1890ff", ...khmerFont }}>
              <TeamOutlined /> {staffList.length}
            </div>
          </div>
        </div>

        {/* Staff List Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <Title level={5} style={{ margin: 0, ...khmerFont }}>
              <TeamOutlined /> បញ្ជីបុគ្គលិក
            </Title>
            <div>
              <Badge count={activeStaff.length} style={{ backgroundColor: "#52c41a" }} />
              <span style={{ marginLeft: 8, ...khmerFont }}>សកម្ម</span>
            </div>
            {inactiveStaff.length > 0 && (
              <div>
                <Badge count={inactiveStaff.length} />
                <span style={{ marginLeft: 8, ...khmerFont }}>មិនសកម្ម</span>
              </div>
            )}
          </div>
          <Input
            placeholder="ស្វែងរក / Search by name, position, job status..."
            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 280, ...mixedFont }}
          />
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 300,
            }}
          >
            <GetLoading />
          </div>
        ) : staffList.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={khmerFont}>
                មិនមានបុគ្គលិកក្នុងផ្នែកនេះទេ
              </span>
            }
            style={{ marginTop: 40 }}
          />
        ) : (
          <Tabs activeKey={activeTab} onChange={setActiveTab} style={mixedFont}>
            <TabPane
              tab={
                <span>
                  <Badge status="success" />
                  សកម្ម ({activeStaff.length})
                </span>
              }
              key="active"
            >
              {renderStaffTable(activeStaff)}
            </TabPane>
            {inactiveStaff.length > 0 && (
              <TabPane
                tab={
                  <span>
                    <Badge status="default" />
                    មិនសកម្ម ({inactiveStaff.length})
                  </span>
                }
                key="inactive"
              >
                {renderStaffTable(inactiveStaff)}
              </TabPane>
            )}
            <TabPane
              tab={
                <span>
                  <TeamOutlined />
                  ទាំងអស់ ({filteredStaff.length})
                </span>
              }
              key="all"
            >
              {renderStaffTable(filteredStaff)}
            </TabPane>
          </Tabs>
        )}
      </div>
    </Modal>
  );
};

export default DepartmentStaffModal;